import { OVERLAY_MS } from "./overlay.ts"

/**
 * A shared slider, tempo, Temperature or swing, as this page shows it. The room's value
 * is whatever the last `ctl` said; there are no locks, and the last write wins.
 *
 * The dragging hand wins locally until release. On release the slider holds the
 * released value until a `ctl` from you carries it (confirmed), a `ctl` from
 * someone else changes it (they win), or 2 s pass (expired), the same rule and
 * the same 2 s as a painted Cell. Echoes of values you dragged through do not
 * match, so they are ignored. A snapshot clears the hold.
 *
 * A `ctl` carries every knob and says who moved one, not which. So someone
 * else's `ctl` counts as their write to this knob only when it changes this
 * knob's value; otherwise they moved another one. Pure functions only, each
 * handing the same value back when nothing changed.
 */

export interface Slider {
  /** The room's value, as the last `ctl` or snapshot said. */
  readonly shared: number
  /** The hand's value while it drags. */
  readonly dragging: number | undefined
  /** The released value, and the page time it was let go, until confirmed, overwritten or expired. */
  readonly held: { readonly value: number; readonly at: number } | undefined
}

export const sliderAt = (shared: number): Slider => ({ shared, dragging: undefined, held: undefined })

/** What the slider and its readout show. */
export const shown = (slider: Slider): number => slider.dragging ?? slider.held?.value ?? slider.shared

/** The hand moved it. */
export const drag = (slider: Slider, value: number): Slider => ({ ...slider, dragging: value })

/** The hand let go. Nothing dragged, nothing to hold. */
export const release = (slider: Slider, at: number): Slider =>
  slider.dragging === undefined
    ? slider
    : { shared: slider.shared, dragging: undefined, held: { value: slider.dragging, at } }

/**
 * A `ctl`, with this knob's value in it and whether it was your own. `flash` is
 * true when someone else changed this knob, which the page shows in the accent.
 */
export const hearCtl = (
  slider: Slider,
  value: number,
  own: boolean
): { readonly slider: Slider; readonly flash: boolean } => {
  const theirs = !own && value !== slider.shared
  const confirmed = own && slider.held?.value === value
  if (value === slider.shared && !confirmed) return { slider, flash: false }
  return {
    slider: { ...slider, shared: value, held: theirs || confirmed ? undefined : slider.held },
    flash: theirs
  }
}

/** Nothing unconfirmed stays on screen longer than 2 s. */
export const expireHold = (slider: Slider, now: number): Slider =>
  slider.held !== undefined && now - slider.held.at >= OVERLAY_MS ? { ...slider, held: undefined } : slider

/** A snapshot: the room's value, and no hold. A drag in progress is still the hand's. */
export const resync = (slider: Slider, value: number): Slider => ({
  ...slider,
  shared: value,
  held: undefined
})
